// Cache for storing fetched employee data
let employeeCache = {
  data: [],
  timestamp: null,
  isLoading: false,
  pendingPromise: null,
};

// Cache expiration time in milliseconds (5 minutes)
const CACHE_EXPIRY = 5 * 60 * 1000;

// Global loading state handler
let loadingHandlers = [];

// Register a loading state handler
export const registerEmployeeLoadingHandler = (handler) => {
  loadingHandlers.push(handler);
  return () => {
    loadingHandlers = loadingHandlers.filter((h) => h !== handler);
  };
};

// Update all registered handlers with the current loading state
const updateLoadingState = (isLoading, message = null) => {
  loadingHandlers.forEach((handler) => handler(isLoading, message));
};

// Utility function to fetch employees from Zoho Creator
export const fetchEmployees = async (forceRefresh = false) => {
  // Return cached data if available and not expired and not forced to refresh
  const now = Date.now();
  if (
    !forceRefresh &&
    employeeCache.data.length > 0 &&
    employeeCache.timestamp &&
    now - employeeCache.timestamp < CACHE_EXPIRY
  ) {
    console.log("Returning cached employee data");
    return employeeCache.data;
  }

  // If a request is already in progress, return the pending promise
  if (employeeCache.isLoading && employeeCache.pendingPromise) {
    console.log(
      "Employee request already in progress, waiting for it to complete"
    );
    return employeeCache.pendingPromise;
  }

  const conn_name = "crmwidgetconnection";
  const apiUrl =
    "https://www.zohoapis.com/creator/v2.1/data/sst1source/source-erp/report/All_Employees";

  const req_data = {
    parameters: {},
    headers: {
      "Content-Type": "application/json",
    },
    method: "GET",
    url: apiUrl,
    param_type: 1,
  };

  // Set loading state
  employeeCache.isLoading = true;
  updateLoadingState(true, "Loading employee data...");

  // Create and store the promise
  employeeCache.pendingPromise = new Promise(async (resolve, reject) => {
    try {
      console.log("Fetching fresh employee data from API...");
      const response = await ZOHO.CRM.CONNECTION.invoke(conn_name, req_data);

      console.log("Employee API Response:", response);

      if (
        !response ||
        response.code !== "SUCCESS" ||
        !response.details ||
        !response.details.statusMessage ||
        !response.details.statusMessage.data
      ) {
        console.error("Invalid employee response structure:", response);
        // Return cached data if available when API fails
        if (employeeCache.data.length > 0) {
          resolve(employeeCache.data);
        } else {
          resolve([]);
        }
        return;
      }

      const employees = response.details.statusMessage.data;

      // Update cache
      employeeCache.data = employees;
      employeeCache.timestamp = Date.now();

      // Add a small delay to show the loader even for quick responses
      setTimeout(() => {
        updateLoadingState(false);
        resolve(employees);
      }, 500);
    } catch (error) {
      console.error("Error fetching employees:", error);
      // Return cached data if available when API fails
      if (employeeCache.data.length > 0) {
        resolve(employeeCache.data);
      } else {
        resolve([]);
      }
    } finally {
      // Clear loading state (after timeout)
      setTimeout(() => {
        employeeCache.isLoading = false;
        employeeCache.pendingPromise = null;
        updateLoadingState(false);
      }, 500);
    }
  });

  return employeeCache.pendingPromise;
};

// Function to invalidate cache (use after updates)
export const invalidateEmployeeCache = () => {
  console.log("Invalidating employee cache");
  employeeCache.timestamp = null;
};
